import { Component, OnInit, AfterViewInit, Optional, Output, EventEmitter } from '@angular/core';
import {
    AbstractControl,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { SharedService } from 'src/app/layouts/shared-service';
import { GroupsService } from 'src/app/services/groups.service';
import { validateEmail } from 'src/utils/email';
import { AlignmentEnum, AlignmentStyleMap } from 'src/app/enums/logo-alignment.enum';

interface EmailTemplateDialogData {
    emailContentHtml?: string;
    fromAddress?: string;
    logoUrl?: string;
    replyTo?: string;
    subject?: string;
    logoAlignment?: number;
}

interface EmailTemplateUpdateResult {
    emailContentHtml: string;
    fromAddress: string;
    replyTo: string;
    subject: string;
    logoAlignment: number;
}

interface LogoUploadResponse {
    message: string;
}

@Component({
    providers: [SharedService],
    selector: 'app-group-analytics-email-template-dialog',
    styleUrls: ['./group-analytics-email-template-dialog.component.scss'],
    templateUrl: './group-analytics-email-template-dialog.component.html',
})
export class GroupAnalyticsEmailTemplateDialogComponent implements AfterViewInit {
    logo: File = null;
    isLogoUploading = false;
    logoUrl = '';
    logoAlignment = AlignmentEnum.CENTER;
    logoAlignmentStyle = '';

    form: UntypedFormGroup;
    AlignmentEnum = AlignmentEnum;

    // Data properties - will be set by dialog wrapper
    emailContentHtml: string = '';
    fromAddress: string = '';
    replyTo: string = '';
    subject: string = '';

    // Optional data object - will be set by dialog wrapper
    data: EmailTemplateDialogData;

    // EventEmitters for dialog communication
    @Output() updateEmail = new EventEmitter<EmailTemplateUpdateResult>();
    @Output() cancelDialog = new EventEmitter<void>();

    constructor(
        @Optional() private readonly sharedService: SharedService,
        private readonly groupsService: GroupsService
    ) {
        // Initialize form in constructor to ensure it's available when template renders
        this.form = new UntypedFormGroup({
            emailContentHtml: new UntypedFormControl(''),
            fromAddress: new UntypedFormControl('', [
                this.fromAddressValidator,
            ]),
            replyTo: new UntypedFormControl(
                '',
                Validators.pattern(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                )
            ),
            subject: new UntypedFormControl('', Validators.required),
        });
    }

    ngAfterViewInit(): void {
        // Update form and component properties after data is set by wrapper
        // Use setTimeout to ensure data has been set by the wrapper
        setTimeout(() => {
            // Check both data object and direct properties (wrapper sets both)
            const dataSource = this.data || {};
            
            // Update component properties from data object or direct properties
            this.emailContentHtml = dataSource.emailContentHtml || this.emailContentHtml || '';
            this.fromAddress = dataSource.fromAddress || this.fromAddress || '';
            this.replyTo = dataSource.replyTo || this.replyTo || '';
            this.subject = dataSource.subject || this.subject || '';
            this.logoUrl = dataSource.logoUrl || this.logoUrl || '';
            this.logoAlignment = dataSource.logoAlignment !== undefined 
                ? dataSource.logoAlignment 
                : (this.logoAlignment !== undefined ? this.logoAlignment : AlignmentEnum.CENTER);
            
            // Update form values
            if (this.form) {
                this.form.patchValue({
                    emailContentHtml: this.emailContentHtml,
                    fromAddress: this.fromAddress,
                    replyTo: this.replyTo,
                    subject: this.subject,
                });
            }
            
            this.logoAlignmentStyle = AlignmentStyleMap[this.logoAlignment];
        }, 0);
    }

    onFileChange(event) {
        this.logo = null;
        if (event.target.files && event.target.files.length > 0) {
            // Set theFile property
            this.logo = event.target.files[0];
        }
    }

    uploadFile(): void {
        if (this.logo != null) {
            this.isLogoUploading = true;

            const file = {
                contentType: this.logo.type,
                fileData: null as string | null,
                // Set File Information
                fileName: this.logo.name,
                groupId: this.sharedService?.currentUser?.groupId,
            };

            // Use FileReader() object to get file to upload
            // NOTE: FileReader only works with newer browsers
            const reader = new FileReader();

            // Setup onload event for reader
            reader.onload = () => {
                // Store base64 encoded representation of file
                file.fileData = reader.result as string;

                // POST to server
                this.groupsService.uploadLogo(file).subscribe((resp: LogoUploadResponse) => {
                    this.isLogoUploading = false;
                    this.logoUrl = resp.message;
                });
            };

            // Read the file
            reader.readAsDataURL(this.logo);
        }
    }

    deleteFile(): void {
        if (this.sharedService?.currentUser?.groupId) {
            this.groupsService
                .deleteLogo(this.sharedService.currentUser.groupId)
                .subscribe(() => {
                    this.logoUrl = '';
                });
        }
    }

    update(): void {
        const result: EmailTemplateUpdateResult = {
            emailContentHtml: this.form.value.emailContentHtml || '',
            fromAddress: this.form.value.fromAddress || '',
            replyTo: this.form.value.replyTo || '',
            subject: this.form.value.subject || '',
            logoAlignment: this.logoAlignment
        };
        
        this.updateEmail.emit(result);
    }

    cancel(): void {
        this.cancelDialog.emit();
    }

    fromAddressValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if (!value) {
            return null;
        }

        return validateEmail(value + '@fbolinx.com')
            ? null
            : { emailNotValid: true };
    }

    alignLogo(align: AlignmentEnum): void {
        this.logoAlignment = align;
        this.logoAlignmentStyle = AlignmentStyleMap[align];
    }
}
